import React, { useState } from "react";
import { Button, Form, InputGroup, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { InputOTP } from "antd-input-otp";
import { useGoogleLogin } from "./store/store";
import { toast } from "react-toastify";
import trayaLogo1 from "../../../src/assets/img/illustrations/starfleet_light.png";

function ResetPassword() {
  const { id } = useParams();
  const [passwordData, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [step, setStep] = useState("password"); // "password" | "otp"
  const [otpValue, setOtpValue] = useState([]);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const { resetPassword, sendResetPasswordOtp } = useGoogleLogin();
  const navigate = useNavigate();

  // Mirrors the backend policy in server/utils/passwordPolicy.js — the
  // backend remains the source of truth (it also checks generic passwords
  // and cross-user uniqueness, which can't be validated client-side)
  const hasSequentialRun = (value) => {
    const chars = value.toLowerCase();
    for (let i = 0; i + 2 < chars.length; i++) {
      const triple = chars.slice(i, i + 3);
      if (!/^\d{3}$/.test(triple) && !/^[a-z]{3}$/.test(triple)) continue;
      const a = chars.charCodeAt(i);
      const b = chars.charCodeAt(i + 1);
      const c = chars.charCodeAt(i + 2);
      if ((b - a === 1 && c - b === 1) || (b - a === -1 && c - b === -1)) {
        return true;
      }
    }
    return false;
  };

  const validatePasswords = () => {
    const rules = [
      [!passwordData?.length, "Please enter a password"],
      [passwordData.length < 8, "Password must be at least 8 characters"],
      [!/[a-z]/.test(passwordData), "Password must contain a lowercase letter"],
      [!/[A-Z]/.test(passwordData), "Password must contain an uppercase letter"],
      [!/\d/.test(passwordData), "Password must contain a number"],
      [!/[^A-Za-z0-9]/.test(passwordData), "Password must contain a special character"],
      [
        hasSequentialRun(passwordData),
        "Password must not contain sequential characters like abc, 123 or 543",
      ],
      [passwordData !== confirmPassword, "Passwords do not match"],
    ];
    const failed = rules.find(([broken]) => broken);
    setPasswordError(failed ? failed[1] : "");
    return !failed;
  };

  const handleSendOtp = async () => {
    if (!validatePasswords()) return;
    setIsSendingOtp(true);
    const { success, devOtp } = await sendResetPasswordOtp(id, toast);
    setIsSendingOtp(false);
    if (success) {
      setOtpValue(devOtp ? devOtp.split("") : []);
      setStep("otp");
    }
  };

  const handleVerifyAndReset = async () => {
    if (otpValue?.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    setIsVerifying(true);
    const success = await resetPassword(
      id,
      { password: passwordData, otp: otpValue.join("") },
      toast,
      navigate
    );
    setIsVerifying(false);
    if (!success) setOtpValue([]);
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center h-100"
      style={{ height: "100", marginTop: "15%" }}
    >
      <div>
        <div
          className="card p-2 shadow"
          style={{ width: "30rem", minHeight: "30rem" }}
        >
          <div className="d-flex justify-content-center">
            <div className="circle-container">
              <div
                className="text-white d-flex align-items-center justify-content-center"
                style={{
                  display: "inline-block",
                  width: "150px",
                  height: "150px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  backgroundColor: "black",
                }}
              >
                <img
                  style={{ width: "8rem", filter: "brightness(0) invert(1)" }}
                  src={trayaLogo1}
                />
              </div>
            </div>
          </div>
          {step === "password" ? (
            <>
              <h4 className="mx-4 bg-white text-danger p-2 border-bottom border-danger ">
                Please Enter New Password
              </h4>
              <Row className="mb-3 mx-3 mt-3">
                <Form.Group md="5" controlId="resetPassword">
                  <InputGroup hasValidation>
                    <Form.Control
                      name="password"
                      type="password"
                      placeholder="New Password"
                      aria-label="New Password"
                      aria-describedby="inputGroupPrepend"
                      value={passwordData}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Row>
              <Row className="mb-2 mx-3">
                <Form.Group md="5" controlId="resetConfirmPassword">
                  <InputGroup hasValidation>
                    <Form.Control
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm New Password"
                      aria-label="Confirm New Password"
                      aria-describedby="inputGroupPrepend"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      isInvalid={
                        confirmPassword.length > 0 &&
                        passwordData !== confirmPassword
                      }
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Passwords do not match.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>
              {passwordError && (
                <p className="mx-3 mb-2 text-danger">{passwordError}</p>
              )}
              <Row className="text-center d-flex justify-content-around p-2">
                <Button
                  variant="success"
                  className="w-50"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? "Sending OTP…" : "Verify via OTP"}
                </Button>
              </Row>
            </>
          ) : (
            <>
              <h4 className="mx-4 bg-white text-danger p-2 border-bottom border-danger ">
                Please Enter OTP
              </h4>
              <p className="mx-4 mb-2 text-muted">
                An OTP has been sent to your registered mobile number.
              </p>
              <InputOTP onChange={setOtpValue} value={otpValue} />
              <div className="mx-5 mt-4 d-flex gap-3 text-center">
                <Button
                  className="w-50"
                  variant="outline-secondary"
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || isVerifying}
                >
                  {isSendingOtp ? "Sending…" : "Resend OTP"}
                </Button>
                <Button
                  className="w-50"
                  variant="success"
                  onClick={handleVerifyAndReset}
                  disabled={isVerifying || otpValue?.length !== 6}
                >
                  {isVerifying ? "Verifying…" : "Verify & Reset Password"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
